import { Link } from 'react-router-dom';
import {
  ArrowRight,
  HardDrive,
  Shield,
  Truck,
  Wrench,
  Lightbulb,
  Laptop,
  Monitor,
  Printer,
  Keyboard,
  Server,
  Briefcase,
  Cpu,
  Wifi,
} from 'lucide-react';
import ADVIES_IMG from '../assets/images/Dgitaal_advies.jpg';

const serviceHighlights = [
  { icon: Shield, title: 'Garantie & Support', desc: 'Uitgebreide garantie opties en technische ondersteuning.' },
  { icon: Truck, title: 'Snelle Levering', desc: 'Levering binnen België, vaak binnen 1-2 werkdagen.' },
  { icon: Wrench, title: 'Installatie Service', desc: 'Wij installeren en configureren uw hardware op locatie.' },
  { icon: Lightbulb, title: 'Advies op Maat', desc: 'Persoonlijk advies afgestemd op uw behoeften en budget.' },
];

const particulieren = [
  { icon: Laptop, title: 'Laptops', desc: 'Krachtige laptops voor werk en privé, van budget tot premium.', brands: 'HP, Dell, Lenovo, ASUS' },
  { icon: Monitor, title: 'Desktops & Monitors', desc: 'Complete desktop systemen en hoogwaardige monitoren.', brands: 'HP, Dell, LG, Samsung' },
  { icon: Printer, title: 'Printers & Scanners', desc: 'Inkjet, laser en multifunctional printers voor thuisgebruik.', brands: 'HP, Canon, Epson, Brother' },
  { icon: Keyboard, title: 'Accessoires', desc: 'Toetsenborden, muizen, webcams, headsets en meer.', brands: 'Logitech, Microsoft, Jabra' },
];

const bedrijven = [
  { icon: Server, title: 'Servers & Storage', desc: 'Enterprise servers, NAS systemen en storage oplossingen.', brands: 'Dell EMC, HP Enterprise, Synology, QNAP' },
  { icon: Laptop, title: 'Zakelijke Laptops', desc: 'Business-class laptops met uitgebreide garantie en support.', brands: 'HP EliteBook, Dell Latitude, Lenovo ThinkPad' },
  { icon: Cpu, title: 'Werkstations', desc: 'High-performance werkstations voor CAD, design en development.', brands: 'HP Z Series, Dell Precision, Lenovo ThinkStation' },
  { icon: Wifi, title: 'Netwerkapparatuur', desc: 'Routers, switches, access points en firewalls.', brands: 'Cisco, Ubiquiti, Fortinet, Aruba' },
];

const businessExtras = [
  { title: 'Volume Kortingen', desc: 'Aantrekkelijke prijzen bij afname van meerdere toestellen. Vraag een offerte op maat.' },
  { title: 'Lease Opties', desc: 'Hardware leasen in plaats van kopen? Wij bieden flexibele leaseconstructies aan.' },
  { title: 'Lifecycle Management', desc: 'Wij beheren de volledige levenscyclus van uw hardware, van aanschaf tot vervanging.' },
];

const brands = ['HP', 'Dell', 'Lenovo', 'Microsoft', 'Cisco', 'Logitech', 'Samsung', 'ASUS'];

export default function Hardware() {
  return (
    <div data-testid="hardware-page">
      <section className="relative pt-40 pb-24 overflow-hidden" style={{ background: 'linear-gradient(135deg, #16224b 0%, #233a80 58%, #f2a100 100%)' }}>
        <div className="dot-pattern absolute inset-0" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <h1 className="text-white font-bold tracking-tight mb-4" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', letterSpacing: '-0.03em' }}>
            Kwaliteitshardware voor Thuis & Bedrijf
          </h1>
          <p className="text-white/70 max-w-xl mx-auto text-base leading-8">
            Van laptops tot servers - wij leveren betrouwbare hardware met persoonlijk advies,
            installatie en support. Voor particulieren én bedrijven.
          </p>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {serviceHighlights.map((item) => (
              <div key={item.title} className="admin-surface p-7 text-center">
                <div className="icon-container mx-auto mb-4">
                  <item.icon className="w-5 h-5" />
                </div>
                <h3 className="text-[#16224b] text-base font-bold mb-2">{item.title}</h3>
                <p className="text-[#6b7184] text-sm leading-7">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#f8f9fd]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="text-[#16224b] font-bold mb-3" style={{ fontSize: 'clamp(1.75rem, 3vw, 2.4rem)' }}>
              Voor Particulieren
            </h2>
            <p className="text-[#6b7184]">Hardware voor thuis en persoonlijk gebruik</p>
          </div>

          <div className="grid md:grid-cols-2 gap-7 mb-10">
            {particulieren.map((item) => (
              <div key={item.title} className="bg-white border border-gray-100 rounded-lg p-7">
                <div className="icon-container mb-4">
                  <item.icon className="w-5 h-5" />
                </div>
                <h3 className="text-[#16224b] text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-[#6b7184] text-sm leading-7 mb-3">{item.desc}</p>
                <p className="text-[#16224b] text-xs font-semibold">{item.brands}</p>
              </div>
            ))}
          </div>

          <div className="admin-surface p-8 grid lg:grid-cols-[1fr_240px] gap-8 items-center">
            <div>
              <h3 className="text-[#16224b] text-xl font-bold mb-3">Persoonlijk Advies Nodig?</h3>
              <p className="text-[#6b7184] leading-8 mb-6">
                Niet zeker welke laptop of computer het beste bij u past? Wij helpen u graag met
                eerlijk advies - zonder verkooppraatjes.
              </p>
              <Link to="/contact" className="btn-gradient">
                Vraag Advies Aan <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="rounded-lg overflow-hidden border border-gray-100">
              <img src={ADVIES_IMG} alt="Persoonlijk hardware advies" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="text-[#16224b] font-bold mb-3" style={{ fontSize: 'clamp(1.75rem, 3vw, 2.4rem)' }}>
              Voor Bedrijven
            </h2>
            <p className="text-[#6b7184]">Professionele hardware met zakelijke voorwaarden</p>
          </div>

          <div className="grid md:grid-cols-2 gap-7 mb-10">
            {bedrijven.map((item) => (
              <div key={item.title} className="bg-[#f8f9fd] border border-gray-100 rounded-lg p-7">
                <div className="icon-container mb-4">
                  <item.icon className="w-5 h-5" />
                </div>
                <h3 className="text-[#16224b] text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-[#6b7184] text-sm leading-7 mb-3">{item.desc}</p>
                <p className="text-[#16224b] text-xs font-semibold">{item.brands}</p>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {businessExtras.map((item) => (
              <div key={item.title} className="admin-surface p-6">
                <h4 className="text-[#16224b] text-base font-bold mb-2">{item.title}</h4>
                <p className="text-[#6b7184] text-sm leading-7">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#f8f9fd]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-[#16224b] font-bold mb-10" style={{ fontSize: 'clamp(1.75rem, 3vw, 2.4rem)' }}>
            Onze Merken
          </h2>
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {brands.map((brand) => (
              <span key={brand} className="px-4 py-2 rounded-full bg-white border border-gray-100 text-[#16224b] text-sm font-medium">
                {brand}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white text-center">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-[#16224b] font-bold mb-4" style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>
            Op zoek naar hardware?
          </h2>
          <p className="text-[#6b7184] mb-8">
            Neem contact op voor een vrijblijvende offerte of persoonlijk advies.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-gradient">
              Vraag Offerte Aan <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="tel:+32470416675" className="btn-outline-dark">
              Bel Ons
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
